import React, { useEffect, useCallback, useMemo, useRef, useState, Children } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { useSelection } from '@fs/zion-frontend-friends'
import Checkbox from '../../checkbox'

const getChildId = (child) => {
  const childId =
    child?.props?.name ??
    child?.key ??
    `${child?.props?.primaryText ?? ''}${child?.props?.secondaryText ? ` ${child?.props?.secondaryText}` : ''}`

  // Remove ".$" prefix if it exists to help more easily interface with these values
  return childId?.startsWith('.$') ? childId.slice(2) : childId
}

const getNewItems = (children) => Children.toArray(children)?.map((child) => ({ id: getChildId(child) }))

const getCheckBox = (name) => document.querySelector(`input[name="${name}"][type="checkbox"]`)

// Without stopping propagation, the click handler on ListItem fires when you click on the checkbox
const stopProp = (e) => e.stopPropagation()

/**
 * @hook useCheckboxList - Manage state for checkbox collections
 *
 * @param {Function} onSelectionChange - Callback that receives an array of names from the selected checkboxes (when that list changes)
 * @param {string} selectAllLabel - label for the main SelectAll checkbox
 * @param {Boolean} initialAllSelected - `true` if all checkboxes start out checked
 * @param {string[]} initialSelection - array of names of individual checkboxes start out checked
 * @param {*} children - know if the checkbox collection items need refreshing. This way we can disregard old checkbox states for removed children.
 *
 * @returns {getPropsForCheckbox<Function>, selectAllCheckbox<Checkbox>}
 */
export function useControlledCheckboxList({
  onSelectionChange,
  selectAllLabel = '',
  children,
  initialAllSelected = false,
  initialSelection = [],
  selectionLimit = Infinity,
  items,
  setItems,
  // from useSelection hook
  idSelectionStates,
  selectItem,
  selectAll,
} = {}) {
  const { t } = useTranslation()

  const shiftPressedRef = useRef(false)
  const lastTouchedRef = useRef(null)
  const checkboxNames = useRef(new Set())
  const getPropsForCheckbox = useRef(() => {})

  const currentlyRenderedCheckboxes = useMemo(() => new Set(), [])
  const itemIds = useMemo(() => items?.map((item) => item?.id), [items])
  const childIds = useMemo(() => Children.toArray(children)?.map((child) => getChildId(child)), [children])
  const checkedItems = useMemo(
    () =>
      Object.keys(idSelectionStates)?.reduce(
        (itemMap, id) => ({ ...itemMap, [id]: idSelectionStates?.[id] && !getCheckBox(id)?.disabled }),
        {}
      ),
    [idSelectionStates]
  )

  const size = childIds?.length ?? 0
  const checkedCount = childIds?.filter((id) => checkedItems?.[id])?.length ?? 0
  const nonDisabledChildren = childIds?.filter((id) => !getCheckBox(id)?.disabled)
  const allSelected = size > 0 && nonDisabledChildren?.every((id) => checkedItems?.[id])
  const mixedState = checkedCount > 0 && !allSelected
  const disableSelectAll = (size < 1 || selectionLimit !== Infinity) && checkedCount === 0

  const changeCallback = useCallback(
    ({ currentTarget: { checked, name } }) => {
      lastTouchedRef.current = { name, checked }
      selectItem(name, shiftPressedRef.current)
    },
    [selectItem]
  )

  const handleKeyDown = useCallback(
    (e, disabled) => {
      if (e.key === 'Shift') {
        shiftPressedRef.current = true
      } else if (e.key === 'Enter' && !disabled) {
        changeCallback(e)
      }
    },
    [changeCallback]
  )

  const handleKeyUp = useCallback((e) => {
    if (e.key === 'Shift') {
      shiftPressedRef.current = false
    }
  }, [])

  const updateMultiple = useCallback(
    ({ names, checked }) => {
      let namesToUpdate = names?.filter((name) => checkedItems?.[name] !== checked && !getCheckBox(name)?.disabled)
      if (selectionLimit !== Infinity && checked) {
        const itemsToCheck = selectionLimit - checkedCount
        namesToUpdate = itemsToCheck > 0 ? namesToUpdate?.slice(0, itemsToCheck) : []
      }
      namesToUpdate?.forEach(selectItem)
    },
    [checkedCount, checkedItems, selectItem, selectionLimit]
  )

  /**
   * @method getPropsForCheckbox - Get all the managed props for a Checkbox.
   * It is assumed that getPropsForCheckbox will get called every render for each checkbox,
   * which is how the internal `checkboxNames` Set gets populated with all the current checkbox names
   *
   * @param {string} name - the name of the checkbox which is required for this hook to manage the state
   * @param {Boolean} [disabled] - true if the checkbox should be disabled
   *
   * @example // apply the output of getPropsForCheckbox directly to a Checkbox
   * const { getPropsForCheckbox } = useCheckboxList({...})
   * <Checkbox {...getPropsForCheckbox.current({ name: 'item1' })}>
   */
  getPropsForCheckbox.current = useCallback(
    ({ name, disabled }) => {
      if (checkedCount >= selectionLimit && !checkedItems[name]) {
        disabled = true
      }
      // If a checkbox is disabled, then we should treat it like it does not exist with regards to the SelectAll behavior
      if (!disabled) {
        currentlyRenderedCheckboxes.add(name)
        checkboxNames.current.add(name)
      }
      const checked = disabled ? initialSelection?.includes(name) : !!checkedItems[name]
      return {
        name,
        disabled,
        onClick: stopProp,
        onChange: disabled ? undefined : changeCallback,
        onKeyDown: (e) => handleKeyDown(e, disabled),
        onKeyUp: handleKeyUp,
        debounce: 0, // we don't need/want debouncing of checkboxes for this hook
        checked,
        'aria-label': t('select.item.action', 'Select Item'),
      }
    },
    [
      checkedCount,
      selectionLimit,
      checkedItems,
      initialSelection,
      changeCallback,
      handleKeyDown,
      handleKeyUp,
      t,
      currentlyRenderedCheckboxes,
    ]
  )

  /**
   * @method toggleSelectAll - Update the state of all the checkboxes at the same time
   * @param {Boolean} checked - The checkbox state to update the checkboxes too
   *
   * @example // check all the checkboxes
   * const { toggleSelectAll } = useCheckboxList({...})
   * toggleSelectAll(true)
   */
  const toggleSelectAll = useCallback(
    (checked) => {
      /**
       * If there is a selectionLimit then we want to allow the user to still deselect all.
       * Otherwise the select all checkbox is disabled when there is a selection limit.
       */
      if (checked && selectionLimit === Infinity) {
        childIds?.forEach((id) => {
          if (!checkedItems?.[id] && !getCheckBox(id)?.disabled) {
            selectItem(id)
          }
        })
      } else {
        childIds?.forEach((id) => {
          if (checkedItems?.[id] && !getCheckBox(id)?.disabled) {
            selectItem(id)
          }
        })
      }
    },
    [checkedItems, childIds, selectItem, selectionLimit]
  )

  // Watch for the children to change so we can remove or add names to allNames as needed.
  useEffect(() => {
    // Each time the children change, clear any checkboxes from currentlyRenderedCheckboxes that are not on the page anymore.
    Array.from(currentlyRenderedCheckboxes.values()).forEach((name) => {
      if (!getCheckBox(name)) {
        currentlyRenderedCheckboxes.delete(name)
      }
    })

    /**
     * Since we optimized and made `checkboxNames` a ref, we have to manage the state changes ourselves.
     * `currentlyRenderedCheckboxes` is always up to date, but only ever used here when we know we will need
     * to re render all the checkboxes.
     */
    checkboxNames.current = currentlyRenderedCheckboxes
    // eslint-disable-next-line react-hooks/exhaustive-deps -- See above
  }, [children])
  // Putting children in the dependency list causes it to update at appropriate times.
  // Because checkboxNames is reset every time children changes, putting checkboxNames in the dependency list here causes an infinite loop.

  useEffect(() => {
    if (onSelectionChange) {
      onSelectionChange(
        Object.entries(checkedItems)
          .filter(([k, v]) => !!v && !getCheckBox(k)?.disabled)
          .map(([k]) => k),
        lastTouchedRef.current
      )
    }
  }, [checkedCount, checkedItems, onSelectionChange])

  useEffect(() => {
    if (initialAllSelected && items.length > 0) {
      selectAll()
    }
  }, [initialAllSelected, items, selectAll])

  useEffect(() => {
    // We only want to select these at the beginning. If not, then when the user deselects all, the initial selection remains selected.
    if (initialSelection?.length > 0) {
      initialSelection?.forEach((name) => selectItem(name))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- See above
  }, [])

  useEffect(() => {
    if (childIds?.some((id) => !itemIds.includes(id))) {
      setItems([...items, ...getNewItems(children).filter((newItem) => !itemIds.includes(newItem.id))])
    }
  }, [childIds, children, itemIds, items, setItems])

  return {
    updateMultiple,
    toggleSelectAll,
    getPropsForCheckbox,
    checkedItems,
    allSelected,
    selectAllCheckbox: (
      <Checkbox
        label={selectAllLabel}
        name="select-all"
        aria-label={selectAllLabel || t('select.all.action', 'Select All')}
        checked={allSelected}
        onChange={({ currentTarget: { checked } }) => toggleSelectAll(checked)}
        mixedState={mixedState}
        disabled={disableSelectAll}
      />
    ),
  }
}

export default function useCheckboxList({ ...props }) {
  const [items, setItems] = useState([])

  return useControlledCheckboxList({
    items,
    setItems,
    ...useSelection({ items }),
    ...props,
  })
}

// Dummy component to display the props in Storybook. This should get tree shaken out in the real build.
export const UseCheckboxListProps = ({ initialAllSelected = false, ...props }) => (
  <div initialAllSelected={initialAllSelected} {...props} />
)

UseCheckboxListProps.propTypes = {
  /** Callback that receives an array of names from the selected checkboxes (when that list changes). Of the form: `(checkedNames, lastTouched) => {...}` */
  onSelectionChange: PropTypes.func.isRequired,

  /** The label for the main SelectAll checkbox */
  selectAllLabel: PropTypes.string,

  /** Set to `true` if you want all checkboxes to start out checked */
  initialAllSelected: PropTypes.bool,

  /** Provide an array of names of individual checkboxes you want to start out checked */
  initialSelection: PropTypes.arrayOf(PropTypes.string),

  /** Max number of checked boxes allowed. */
  selectionLimit: PropTypes.number,

  /** Used by this hook to know if the checkbox collection items need refreshing. This way we can disregard old checkbox states for removed children.  */
  children: PropTypes.node,
}
